import React, { useEffect, useState } from 'react';
import {
    Divider,
    Grid,
    TextField,
    FormControlLabel,
    Switch,
    FormGroup,
    Typography,
    Paper,
    Avatar,
    Stack,
    Checkbox,
    IconButton,
    InputAdornment,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { WearEarPlugAndGogglesIcon } from '../common/Icons';
import { Notifications, NotificationsOff } from '@mui/icons-material';
import {useNavigate} from "react-router-dom";
import ConfirmDialog from "./ConfirmDialog";

function GHEProduct({ control, register, errors, setValue, getValues, produto, globalPeriod, globalAlert, globalQuantity, allowInteraction, maxPontoAlerta, exibirAlerta }) {
    const navigate = useNavigate();

    const [isChecked, setIsChecked] = useState(getValues(`permissoes[${produto.id}].sobrescreverCategoria`) ?? false)
    const isActive = produto.status === 1 || produto.status === true || produto.status === 'ativo';
    const selectedEmpresaId = produto.id_empresa;

    const [periodo, setPeriodo] = useState(getValues(`permissoes[${produto.id}].periodo`) ?? 1);

    // caso periodo seja diferente de GlobalPeriod, significa que foi alterado pelo usuário
    const [campoAtivado, setCampoAtivado] = useState(globalPeriod !== periodo);

    const [openLeaveDialog, setOpenLeaveDialog] = useState(false);
    const [pendingNavigationUrl, setPendingNavigationUrl] = useState(null);

    const vidaUtilProduto = Number(produto?.vida_util) || null;

    let query = new URLSearchParams({
        id_empresa: selectedEmpresaId,
        page: '1',
        perPage: '10',
        nome: produto.nome,
    });

    if (produto.ca != null) {
        query.set('ca', produto.ca);
    }

    if (produto.cod_externo != null) {
        query.set('cod_externo', produto.cod_externo);
    }

    useEffect(() => {
        register(`permissoes[${produto.id}].status`, { value: isActive });
        register(`permissoes[${produto.id}].sobrescreverCategoria`, { value: isChecked });
    }, [produto.id, isActive, isChecked]);

    useEffect(() => {
        const initialValue = getValues(`permissoes[${produto.id}].sobrescreverCategoria`);
        setIsChecked(initialValue ?? true); // Define o estado inicial baseado no valor do formulário ou true como fallback
    }, [getValues, produto.id]);

    useEffect(() => {
        if (!isChecked) {
            const periodoHerdado = vidaUtilProduto
                ? Math.min(Number(globalPeriod) || 0, vidaUtilProduto)
                : globalPeriod;
            setValue(`permissoes[${produto.id}].periodo`, periodoHerdado);
            setValue(`permissoes[${produto.id}].ponto_alerta`, globalAlert);
        }

        setValue(`permissoes[${produto.id}].quantidade_maxima`, globalQuantity);
    }, [globalAlert, globalPeriod, globalQuantity, isChecked, produto.id, setValue, vidaUtilProduto]);

    return (<>
        <input
            type="hidden"
            {...register(`permissoes[${produto.id}].id_produto`, { value: produto.id })}
        />

        <input
            type="hidden"
            {...register(`permissoes[${produto.id}].quantidade_maxima`, { value: globalQuantity })}
        />

        <Paper sx={{
            mt: 2,
            backgroundColor: isChecked ? 'rgba(255, 252, 230, 1)' : 'inherit',
            opacity: !isActive ? 0.5 : 1,
            pointerEvents: !isActive ? 'none' : 'auto',
            filter: !isActive ? 'grayscale(1)' : 'none',
        }}>
            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="stretch"
                spacing={1}
                sx={{ p: 1 }}
            >
                <Grid item xs={2}>
                    <FormGroup>
                        <Controller
                            name={`permissoes[${produto.id}].status`}
                            control={control}
                            defaultValue={isActive}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size='small'
                                            {...field}
                                            checked={field.value}
                                            disabled={!allowInteraction}
                                            onChange={(event) => {
                                                const newValue = event.target.checked;

                                                const allPermissions = getValues("permissoes") || {};

                                                const categoryId = produto.id_categoria ?? produto.idCategoria;
                                                const sameCategoryPermissions = Object.values(allPermissions).filter(
                                                    (p) => (p.id_categoria === categoryId) && p.status
                                                );

                                                let activeCountInCategory = sameCategoryPermissions.filter(
                                                    (p) => p.status === true || p.status === 1
                                                ).length;

                                                if (!newValue && activeCountInCategory <= 1) {
                                                    exibirAlerta(
                                                        'Ops',
                                                        'Pelo menos uma permissão precisa permanecer ativa. Para remover tudo, apague a categoria inteira',
                                                        'error'
                                                    );
                                                    return;
                                                }

                                                // Atualiza normalmente
                                                field.onChange(newValue);
                                                setValue(`permissoes[${produto.id}].status`, newValue);
                                            }}
                                        />
                                    }
                                    label={field.value ? 'Ativo' : 'Inativo'}
                                />
                            )}
                        />
                    </FormGroup>
                    <Avatar>
                        <WearEarPlugAndGogglesIcon fontSize='large' />
                    </Avatar>
                </Grid>
                <Grid item xs={10}>
                    <Stack
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={1}
                        divider={<Divider orientation="vertical" flexItem />}
                        sx={{ mb: 1 }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 'bold',
                                pt: 1,
                                cursor: 'pointer',
                                textDecoration: 'none',
                                pointerEvents: 'auto',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                            onClick={() => {
                                setPendingNavigationUrl(`/epis/produtos/listadeprodutos?${query.toString()}`);
                                setOpenLeaveDialog(true);
                            }}
                        >
                            {`${produto?.nome}${isActive ? '' : ' - INATIVO'}`}
                        </Typography>
                        <Typography variant='body2' sx={{ pt: 1 }}>
                            <strong>CA:</strong> {produto?.ca}
                        </Typography>
                        <Typography variant='body2' sx={{ pt: 1 }}>
                            <strong>Vida útil:</strong> {produto.vida_util ? `${produto.vida_util} dia(s)` : '--'}
                        </Typography>
                    </Stack>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="stretch"
                        spacing={2}
                    >
                        <Grid item xs={12} md={4}>
                            <Controller
                                name={`permissoes[${produto.id}].sobrescreverCategoria`}
                                control={control}
                                defaultValue={isChecked} // Define um valor padrão para o Controller
                                render={({ field: { onChange, value, ...field } }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...field}
                                                checked={value ?? isChecked} // Usa o valor do formulário ou o valor inicial se undefined
                                                size="small"
                                                onChange={(event) => {
                                                    onChange(event.target.checked); // Atualiza o valor no React Hook Form
                                                    setIsChecked(event.target.checked); // Atualiza o estado local
                                                }}
                                                disabled={!allowInteraction}
                                            />
                                        }
                                        label={<Typography variant='body2'>Sobrescrever dados da categoria</Typography>}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <TextField
                                disabled={!isChecked || !allowInteraction}
                                {...register(`permissoes[${produto.id}].periodo`, {
                                    required: isChecked,
                                    max: vidaUtilProduto
                                        ? { value: vidaUtilProduto, message: `Máx. ${vidaUtilProduto} dia(s)` }
                                        : undefined
                                })}
                                InputLabelProps={{ shrink: true }}
                                margin="dense"
                                size='small'
                                label="Período"
                                defaultValue={1}
                                type='number'
                                fullWidth
                                error={!!errors?.permissoes?.[produto.id]?.periodo}
                                helperText={
                                    errors?.permissoes?.[produto.id]?.periodo?.message
                                    ?? (vidaUtilProduto ? `Máx. ${vidaUtilProduto} dia(s)` : '')
                                }
                                onChange={(e) => {
                                    let value = Number(e.target.value);

                                    if (vidaUtilProduto && value > vidaUtilProduto) {
                                        value = vidaUtilProduto;
                                        exibirAlerta?.(
                                            'Atenção',
                                            `O período deste EPI não pode ultrapassar sua vida útil (${vidaUtilProduto} dia(s)).`,
                                            'warning'
                                        );
                                    }

                                    setValue(`permissoes[${produto.id}].periodo`, value);
                                    setPeriodo(value);
                                }}
                                variant="outlined"
                                inputProps={{ min: 1, max: vidaUtilProduto ?? undefined }}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                disabled={!campoAtivado || !isChecked || !allowInteraction}
                                fullWidth
                                margin="dense"
                                size="small"
                                label="Ponto de Alerta"
                                defaultValue={0}
                                type="number"
                                required
                                name={`permissoes[${produto.id}].ponto_alerta`}
                                autoComplete="off"
                                variant="outlined"
                                {...register(`permissoes[${produto.id}].ponto_alerta`)}
                                inputProps={{ min: 1, max: (maxPontoAlerta - 1) }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton
                                                aria-label="toggle campo visibility"
                                                onClick={() => isChecked && setCampoAtivado((atual) => {
                                                    atual ? setValue(`permissoes[${produto.id}].ponto_alerta`, 0) : setValue(`permissoes[${produto.id}].ponto_alerta`, 1);

                                                    return !atual
                                                })
                                                }
                                                edge="start"
                                                disabled={!allowInteraction}
                                            >
                                                {campoAtivado ? <Notifications /> : <NotificationsOff />} {/* Ícone de cadeado aberto ou fechado */}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>

        <ConfirmDialog
            title="Sair desta página?"
            description={`Ao sair, todas as informações inseridas serão perdidas. Deseja continuar mesmo assim?`}
            state={openLeaveDialog}
            goAction={() => {
                if (pendingNavigationUrl) {
                    navigate(pendingNavigationUrl);
                }
                setOpenLeaveDialog(false);
                setPendingNavigationUrl(null);
            }}
            handleClose={() => {
                setOpenLeaveDialog(false);
                setPendingNavigationUrl(null);
            }}
        />
    </>);
}

export default GHEProduct;